import { formatDate } from "@app/common/utils/utils";
import routes from "@app/config/routes";
import { useGetOrdersList } from "@app/services/drugDelivery/hooks";
import { Medicine, OrderListPatient } from "@app/services/drugDelivery/types";
import { useDeliveryData } from "@app/store/useDeliverySearchData";
import { Box, Card, Flex, Grid, Group, Table, Title, Tooltip } from "@mantine/core";
import {
  IconAlertCircle,
  IconChevronLeft,
  IconCircleCheck,
  IconCircleX,
  IconInfoCircle
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";

const medicalOrderTypeLabels: Record<string, string> = {
  insurance: "Aseguradora",
  particular: "Particular",
  sugess: "Sugerencia"
};

const deliveryStatusConfig: Record<string, { bgColor: string; label: string }> = {
  available: {
    bgColor: "",
    label: "disponible para entrega inmediata"
  },
  partially_delivered: {
    bgColor: "#EDEDED",
    label: "Medicamentos entregados. Próxima entrega programada a partir del"
  },
  delivered: {
    bgColor: "#EDEDED",
    label: "Medicamentos entregados en su totalidad"
  }
};

const OrderPatientList = () => {
  const setShowDelivery = useDeliveryData((store) => store.setDataDispense);
  const { dataPatient } = useDeliveryData((store) => store.dataToSearch);

  const navigate = useNavigate();

  const patientDocument = dataPatient
    ? "document" in dataPatient
      ? dataPatient.document
      : dataPatient
    : "";

  const {
    data: ordersList = {
      results: [],
      rowTotal: 0
    }
  } = useGetOrdersList({
    type: "patient",
    patient: patientDocument
  });

  const renderRow = (option: Medicine, bgColor?: string, idx?: number) => {
    return (
      <Table.Tr key={option?.id} data-testid={`orderpatientlist-medicine-row-${idx}`}>
        <Table.Td bg={bgColor ?? "#fff"}>{option?.code ?? "-"}</Table.Td>
        <Table.Td bg={bgColor ?? "#fff"}>{option?.name}</Table.Td>

        <Table.Td bg={bgColor ?? "#fff"} ta={"center"}>
          {option?.qtyOrder ?? "-"} unds.
        </Table.Td>
        <Table.Td bg={bgColor ?? "#fff"} ta={"center"}>
          {option?.qtyDelivered ?? "-"} unds.
        </Table.Td>
        <Table.Td bg={bgColor ?? "#fff"} ta={"center"}>
          {option?.qtyPending ?? "-"} unds.
        </Table.Td>
      </Table.Tr>
    );
  };

  const renderTable = (medicines: Medicine[], bgColor?: string) => {
    return (
      <Table
        className="biowel-table"
        withTableBorder
        verticalSpacing={"0.3rem"}
        style={{ backgroundColor: bgColor }}
        data-testid="orderpatientlist-medicines-table"
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th bg={bgColor ?? "#fff"} ta={"start"}>
              Código
            </Table.Th>
            <Table.Th bg={bgColor ?? "#fff"}>Nombre</Table.Th>
            <Table.Th bg={bgColor ?? "#fff"} ta={"center"}>
              Cantidad ordenada
            </Table.Th>
            <Table.Th bg={bgColor ?? "#fff"} ta={"center"}>
              Cantidad entregada
            </Table.Th>
            <Table.Th bg={bgColor ?? "#fff"} ta={"center"}>
              Cantidad pendiente
            </Table.Th>
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {medicines ? (
            medicines?.map((medicine, idx) => renderRow(medicine, bgColor, idx))
          ) : (
            <Table.Tr>
              <Table.Td ta="center" colSpan={12}>
                No hay información para mostrar.
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    );
  };

  const handleCard = (option: OrderListPatient) => {
    const { medicalOrderType, orderId, rcdInfo } = option;
    const fullDeliveryIsApproved = rcdInfo?.fullDelivery;
    const isPendingCompleteDelivery = ["Pendiente", "Aprobado"].includes(rcdInfo?.status || "");

    setShowDelivery({
      orderId: Number(orderId),
      fullDeliveryIsApproved,
      isPendingCompleteDelivery,
      ...(medicalOrderType === "insurance" && { showDeliveryBy: true }),
      ...((["particular", "sugess"] as string[]).includes(medicalOrderType) && {
        deliveryBy: "freeSaleWithOrder"
      })
    });

    if (medicalOrderType !== "insurance") {
      navigate(routes.getDeliveryDispensation);
    }
  };

  const renderCard = (option: OrderListPatient, idx: number) => {
    const deliveryStatus = option.deliveryStatus?.value || "available";

    const currentDeliveryStatus = deliveryStatusConfig[deliveryStatus];
    const orderInactive = ["delivered", "partially_delivered"].includes(deliveryStatus);

    return (
      <Card
        key={option.orderId}
        mb={"md"}
        className={orderInactive ? undefined : "pointer"}
        data-testid={`orderpatientlist-order-card-${idx}`}
        p={0}
        onClick={() => {
          if (orderInactive) {
            return;
          }

          handleCard(option);
        }}
      >
        <Box
          bg={currentDeliveryStatus?.bgColor}
          py={"sm"}
          px={"lg"}
          data-testid={`orderpatientlist-card-content-${idx}`}
        >
          <Box
            py={"xs"}
            className="flex justify-between"
            data-testid={`orderpatientlist-card-header-${idx}`}
          >
            <Flex justify={"space-between"} align={"center"} flex={1}>
              <Title size={"h5"} py={0} className="secondary-color">
                <b>
                  Fórmula de medicamentos -{" "}
                  {medicalOrderTypeLabels[option.medicalOrderType] ?? option.medicalOrderType}
                </b>
              </Title>

              <Group>
                <span>{option.deliveryProgress}</span>

                <Tooltip
                  label={
                    deliveryStatus === "partially_delivered"
                      ? `${currentDeliveryStatus?.label} ${formatDate(option.nextDeliveryDate || "")}`
                      : currentDeliveryStatus?.label
                  }
                >
                  <IconInfoCircle
                    color={option.deliveryStatus?.bgColor || "#7bc62d"}
                    style={{ width: 24, height: 24 }}
                    data-testid={`orderpatientlist-status-icon-${idx}`}
                  />
                </Tooltip>
              </Group>
            </Flex>

            {option?.rcdInfo?.status && (
              <Card
                py={0}
                className="flex items-center justify-center"
                data-testid={`orderpatientlist-request-status-${idx}`}
              >
                <div className="flex gap-2">
                  Solicitud de entrega total: <b>{option.rcdInfo?.status}</b>-{" "}
                  {option.rcdInfo?.reviewedAt?.split(" ")[0]}{" "}
                  <Tooltip
                    label={
                      option.rcdInfo?.status === "Rechazada"
                        ? `Solicitud ${option.rcdInfo?.status}: ${option?.rcdInfo?.rcdRejectReason}`
                        : `Solicitud ${option.rcdInfo?.status}`
                    }
                  >
                    <span data-testid={`orderpatientlist-request-icon-${idx}`}>
                      {option.rcdInfo?.status === "Aprobado" ? (
                        <IconCircleCheck color="#7bc62d" />
                      ) : option.rcdInfo?.status === "Pendiente" ? (
                        <IconAlertCircle color="#ff4500" />
                      ) : (
                        <IconCircleX color="#ff2825" />
                      )}
                    </span>
                  </Tooltip>
                </div>
              </Card>
            )}
          </Box>
          <Grid py={"0.2rem"}>
            <Grid.Col span={3}>
              <b>Aseguradora</b>
            </Grid.Col>
            <Grid.Col span={9}>{option?.corporateClient.name}</Grid.Col>
          </Grid>
          <Grid py={"0.2rem"}>
            <Grid.Col span={3}>
              <b>Fecha de orden</b>
            </Grid.Col>
            <Grid.Col span={9}>{option?.createdAtMedicalOrders}</Grid.Col>
          </Grid>
          <Grid py={"0.2rem"}>
            <Grid.Col span={3}>
              <b>Ordenado por</b>
            </Grid.Col>
            <Grid.Col span={9}>{option?.doctor}</Grid.Col>
          </Grid>
          <Grid py={"0.2rem"}>
            <Grid.Col span={3}>
              <b>Validez</b>
            </Grid.Col>
            <Grid.Col span={9}>{option?.validUntil}</Grid.Col>
          </Grid>
          <Grid py={"0.2rem"}>
            <Grid.Col span={3}>
              <b>Observaciones</b>
            </Grid.Col>
            <Grid.Col span={9}>{option?.observations}</Grid.Col>
          </Grid>
          <Box py={"sm"} data-testid={`orderpatientlist-medicines-container-${idx}`}>
            {renderTable(option.medicines, currentDeliveryStatus?.bgColor)}
          </Box>
        </Box>
      </Card>
    );
  };

  return (
    <div className="main-container" data-testid="orderpatientlist-container">
      <Box className="flex justify-between w-100" data-testid="orderpatientlist-header">
        <Box className="flex gap-2">
          <div
            data-testid="orderpatientlist-back-button"
            className="flex items-center pointer"
            onClick={() => navigate(routes.drugDelivery)}
          >
            <Tooltip label="Regresar" withArrow position="right">
              <IconChevronLeft
                style={{ height: 30, width: 30 }}
                className="primary-color pointer"
              />
            </Tooltip>
          </div>
          <Title size={"h3"} py={"xs"} c="secondary-color" fw={"bold"} ta={"center"}>
            Ordenamientos
          </Title>
        </Box>
      </Box>
      <Box w={"100%"} data-testid="orderpatientlist-orders-container">
        {ordersList?.results?.length ? (
          ordersList?.results?.map((order, idx) => renderCard(order, idx))
        ) : (
          <Box>No hay ordenamientos pendientes.</Box>
        )}{" "}
      </Box>
    </div>
  );
};

export default OrderPatientList;
